import { useEffect, useRef } from 'react'
import useSWR from 'swr'
import swrFetcher from '../swrFetcher'
import useSWRInfinite from 'swr/infinite'

function useData(
    url,
    resourceName,
    getValue,
    revalidateOnFocus = false,
    revalidateOnReconnect = false,
    onSuccess,
    fallbackData,
    refreshInterval,
    revalidateOnMount,
    abortOnUmount = false,
    infinite = false,
) {
    const abortControllerRef = useRef(null)

    const options = {
        revalidateOnReconnect,
        revalidateOnFocus,
    }

    if (onSuccess && !infinite) options.onSuccess = onSuccess
    if (refreshInterval) options.refreshInterval = refreshInterval
    if (revalidateOnMount) options.revalidateOnMount = true
    if (infinite) options.parallel = true
    if (infinite) options.revalidateFirstPage = false

    const useSWRFn = infinite ? useSWRInfinite : useSWR

    const {
        data: swrData,
        error: swrError,
        isValidating,
        mutate,
        size,
        setSize,
    } = useSWRFn(
        infinite && url ? getKey : url,
        (url) => {
            abortControllerRef.current = new AbortController()
            return swrFetcher(url, abortControllerRef.current.signal)
        },
        options,
    )

    useEffect(() => {
        if (!swrData) {
            return
        }
        if (revalidateOnFocus || revalidateOnMount || revalidateOnReconnect) {
            return
        }
        if (onSuccess) {
            onSuccess(swrData)
        }
        return function () {
            if (abortOnUmount && abortControllerRef.current) {
                abortControllerRef.current.abort(
                    'Component unmounted, aborting fetch',
                )
            }
        }
    }, [])

    useEffect(() => {
        if (!swrData || !infinite) {
            return
        }
        onSuccess(swrData)
    }, [swrData])

    function getKey(pageIndex) {
        try {
            const urlObj = new URL(url, window.location.origin)
            urlObj.searchParams.set('page', pageIndex + 1)
            const resultStr = `${urlObj.pathname}?${urlObj.searchParams.toString()}`
            return resultStr
        } catch (error) {
            console.error(error)
            return null
        }
    }

    if (!url && fallbackData) {
        if (onSuccess) onSuccess(fallbackData)
        return {
            [resourceName]: fallbackData,
            [`${resourceName}Error`]: null,
            [`${resourceName}Status`]: null,
            [`${resourceName}Loading`]: false,
            [`${resourceName}Mutate`]: () => {},
            [`${resourceName}Validating`]: false,
            ...(infinite
                ? {
                      [`${resourceName}Page`]: 1,
                      [`${resourceName}SetPage`]: () => {},
                  }
                : {}),
        }
    }

    let data = null
    if (infinite && getValue) {
        data = (swrData || []).map(getValue).flat()
    } else if (infinite) {
        data = (swrData || []).flat()
    } else {
        data = getValue ? getValue(swrData) : swrData
    }

    const error = swrError ? swrError?.message || 'Unexpected error' : null
    const loading = url && !swrData && !swrError
    const hasNext = swrData?.[0]?.totalPages > size + 1

    return {
        [resourceName]: data,
        [`${resourceName}Error`]: error,
        [`${resourceName}Status`]: swrError?.status,
        [`${resourceName}Loading`]: loading,
        [`${resourceName}Mutate`]: mutate,
        [`${resourceName}Validating`]: isValidating,
        ...(infinite
            ? {
                  [`${resourceName}Page`]: size,
                  [`${resourceName}SetPage`]: setSize,
                  [`${resourceName}HasNext`]: hasNext,
              }
            : {}),
    }
}

export default useData
